import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider, useLocation } from '@tanstack/react-router';
import { PropsWithChildren, useEffect } from 'react';
import * as React from 'react';

import { LoadingPage } from '@/components/ui';
import { useRudderAnalytics } from '@/hooks';

import { initMixpanel, initSentry } from './instrument';
import { routeTree } from './routeTree.gen';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 10,
    },
  },
});

const PageTracker = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const { analytics } = useRudderAnalytics();
  const { isAuthenticated, getClaims, user } = useKindeAuth();

  useEffect(() => {
    if (isAuthenticated && analytics) {
      getClaims().then((claims) => {
        const profileId = claims?.user_properties?.profile_id?.v;

        if (profileId) {
          analytics?.identify(profileId, {
            email: user?.email,
            firstName: user?.givenName,
            lastName: user?.familyName,
          });
        }
      });
    }
  }, [isAuthenticated, getClaims, user?.email, user?.givenName, user?.familyName, analytics]);

  useEffect(() => {
    if (analytics && location.pathname) {
      analytics.page('Loaded a Page');
    }
  }, [location.pathname, analytics]);

  return <>{children}</>;
};

const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    queryClient: queryClient,
  },
  routeMasks: undefined,
  InnerWrap: ({ children }) => <PageTracker>{children}</PageTracker>,
  defaultPendingComponent: LoadingPage,
});

initSentry(Sentry.tanstackRouterBrowserTracingIntegration(router));
initMixpanel();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

let TanStackQueryDevtools;
if (process.env.NODE_ENV === 'production') {
  TanStackQueryDevtools = () => null;
} else {
  TanStackQueryDevtools = React.lazy(() =>
    import('@tanstack/react-query-devtools').then((res) => ({
      default: res.ReactQueryDevtools,
    }))
  );
}

export const App = () => {
  const auth = useKindeAuth();

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ auth }} />
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
};
